import { createComponentPreviewEntry } from '@wix/editor-elements-integrations';
import {
  usePreviewEffect,
  usePreviewState,
  usePrevious,
  useResetComponent,
} from '@wix/editor-elements-preview-utils';
import type {
  IComponentPreviewWrapper,
  PreviewWrapperProps,
} from '@wix/editor-elements-types/thunderboltPreview';
import React, { useRef } from 'react';
import type {
  RatingsInputPreviewWrapperProps,
  IRatingsInputProps,
  IRatingsInputImperativeActions,
} from '../RatingsInput.types';
import { DEFAULT_SHAPE_SPACING, DEFAULT_SHAPE_SIZE } from './constants';
import { classes } from './RatingsInputItem/RatingsInputItem.component.st.css';

const noop = () => {};

function withComponentPreview(
  WrappedComponent: React.ComponentType<IRatingsInputProps>,
): IComponentPreviewWrapper<
  IRatingsInputProps,
  RatingsInputPreviewWrapperProps
> {
  return React.forwardRef<IRatingsInputImperativeActions, IRatingsInputProps>(
    (
      {
        previewWrapperProps = {},
        ...viewerProps
      }: PreviewWrapperProps<
        IRatingsInputProps,
        RatingsInputPreviewWrapperProps
      >,
      ref,
    ) => {
      const {
        compWidth,
        shouldResetComponent,
        compPreviewState,
        componentViewMode,
        shapeSize = DEFAULT_SHAPE_SIZE,
        shapeSpacing = DEFAULT_SHAPE_SPACING,
      } = previewWrapperProps;
      const {
        id,
        value,
        labelPosition,
        showTitle,
        showLabels,
        onRatingChange = noop,
        setValidityIndication = noop,
        validateValue = noop,
        title,
        showA11yLabel,
      } = viewerProps;
      const isValid = viewerProps.isValid || componentViewMode === 'editor';
      const rootMinWidth =
        !showA11yLabel && !!title && labelPosition !== 'side'
          ? shapeSize * 5 + shapeSpacing * 4
          : undefined;

      const getRating = () => {
        if (componentViewMode === 'editor' && compPreviewState === 'error') {
          return 0;
        }

        return componentViewMode === 'editor' &&
          (value === null || value === undefined)
          ? 3
          : value;
      };

      const previousCompViewMode = usePrevious(componentViewMode);
      const defaultValue = useRef(value);

      React.useEffect(() => {
        if (
          previousCompViewMode === 'editor' &&
          componentViewMode === 'editor'
        ) {
          defaultValue.current = value;
        }
      }, [previousCompViewMode, componentViewMode, value]);

      usePreviewEffect({
        componentViewMode: componentViewMode || '',
        onPreviewViewMode: validateValue,
      });

      const key = useResetComponent({
        shouldResetComponent,
        id,
        onResetComponent: () => {
          onRatingChange({
            target: { value: `${defaultValue.current}` },
          } as React.ChangeEvent<HTMLInputElement>);
          setValidityIndication(false);
          validateValue();
        },
      });

      usePreviewState(id, compPreviewState, {
        focus: {
          selectors: `.${classes.root}`,
          type: 'plural',
          state: 'focus-visible',
        },
        error: {
          selectors: `.${classes.root}`,
          type: 'plural',
        },
      });

      const calcIconsLayout = React.useCallback(() => {
        if (labelPosition === 'side' && (showTitle || showLabels)) {
          return {
            marginInlineEnd: `${Math.max(15, shapeSpacing)}px`,
          };
        }

        return {};
      }, [labelPosition, showTitle, showLabels, shapeSpacing]);

      const reCalculateIconsSizeDeps = React.useMemo(
        () => [shapeSize, shapeSpacing, labelPosition, showLabels, showTitle],
        [shapeSize, shapeSpacing, labelPosition, showLabels, showTitle],
      );

      return (
        <WrappedComponent
          {...viewerProps}
          key={key}
          ref={ref}
          value={getRating()}
          showLabels={showLabels && compPreviewState !== 'error'}
          isValid={isValid}
          reCalculateIconsSizeDeps={reCalculateIconsSizeDeps}
          overrideIconsStyle={calcIconsLayout}
          rootMinWidth={rootMinWidth}
          compWidth={compWidth}
        />
      );
    },
  );
}

export default (ViewerComponent: React.ComponentType<IRatingsInputProps>) =>
  createComponentPreviewEntry(withComponentPreview(ViewerComponent));
